/* eslint-disable react-hooks/exhaustive-deps */
import { useReducer, useMemo, useCallback, useEffect, memo } from 'react';
import CatalogContext from './CatalogContext';
import initialCatalogState from './initialCatalogState';
import * as CatalogTypes from 'contexts/catalog/reducer/catalog.types';
import catalogReducer from 'contexts/catalog/reducer/catalog.reducer';
import * as action from 'contexts/catalog/reducer/catalog.actions';
import { useAuth0 } from '@auth0/auth0-react';
import { initialProductsQuantity } from 'utils/constants';
import { useTranslation } from 'react-i18next';
import { abortCatalogController } from 'utils/abortController';
import {
  IaddLikeActionProps,
  IgetProductProps,
  IremoveLikeActionProps,
  TCatalogFormatView,
  TQuerySearchArray,
} from 'interfaces/catalog.interface';
import useContextUser from 'hooks/contexts/useContextUser';
import useContextMyBrands from 'hooks/contexts/useContextMyBrands';
import { ChildrenProps } from 'interfaces/general.interface';
import useContextCompany from 'hooks/contexts/useContextCompany';
import { IBrand } from 'interfaces/myBrands.interface';

function CatalogProvider(props: ChildrenProps) {
  const [catalogState, dispatch] = useReducer(catalogReducer, initialCatalogState);
  const { getAccessTokenSilently } = useAuth0();
  const { dbUser } = useContextUser();
  const { company } = useContextCompany();
  const { connectedBrands } = useContextMyBrands();
  const { t: translate } = useTranslation();

  useEffect(() => {
    dispatch({ type: CatalogTypes.SET_IS_LOADING_SET_BODY_PRODUCT, payload: true });
    if (
      connectedBrands.length === 0 ||
      !dbUser?.id ||
      !dbUser?.i18n_lang_id ||
      !company?.id
    ) {
      return;
    }
    const associationsId = connectedBrands.map((brand) => brand.tenant.brand.id);
    dispatch({
      type: CatalogTypes.SET_BODY_PRODUCT,
      payload: {
        brandId: associationsId,
        clientId: company.id,
        limit: initialProductsQuantity,
        languageId: dbUser.i18n_lang_id,
      },
    });
    dispatch({ type: CatalogTypes.SET_IS_LOADING_SET_BODY_PRODUCT, payload: false });
  }, [company?.id, connectedBrands.length, dbUser?.i18n_lang_id, dbUser?.id]);

  const getCatalog = useCallback(async () => {
    if (catalogState.bodyProductSearch.options.brand_id.length === 0) {
      return false;
    }
    const signal = await abortCatalogController();
    const token = await getAccessTokenSilently();
    return await action.getCatalogAction({
      dispatch,
      bodyProductSearch: catalogState.bodyProductSearch,
      translate,
      signal,
      token,
    });
  }, [catalogState.bodyProductSearch, getAccessTokenSilently]);

  const getRelatedProducts = useCallback(
    async ({ family }: { family: string }) => {
      const token = await getAccessTokenSilently();
      if (!company?.id || connectedBrands.length === 0) {
        return false;
      }
      const associationsId = connectedBrands.map((brand) => brand.tenant.brand.id);
      return await action.getRelatedProductsAction({
        dispatch,
        clientId: company.id,
        bodyProductSearch: catalogState.bodyProductSearch,
        associationsId,
        family,
        translate,
        token,
      });
    },
    [catalogState.bodyProductSearch, company?.id]
  );

  const addMoreProductsToCatalog = useCallback(async () => {
    if (!catalogState.bodyProductSearchTemp) return false;
    const token = await getAccessTokenSilently();
    return await action.addMoreProductsToCatalogAction({
      dispatch,
      bodyProductSearchTemp: catalogState.bodyProductSearchTemp,
      translate,
      token,
    });
  }, [catalogState.bodyProductSearchTemp]);

  const getProduct = useCallback(
    async ({ productId, brandId }: IgetProductProps) => {
      const token = await getAccessTokenSilently();
      if (!dbUser?.id || !dbUser?.i18n_lang_id) return false;
      return await action.getProductAction({
        dispatch,
        productId,
        brandId,
        languageId: dbUser.i18n_lang_id,
        userId: dbUser.id,
        translate,
        token,
      });
    },
    [dbUser?.id, dbUser?.i18n_lang_id]
  );

  const resetCatalog = useCallback(async () => {
    return dispatch({ type: CatalogTypes.RESET_CATALOG });
  }, []);

  const addToBodySearch = useCallback(
    async (query: string) => {
      const signal = await abortCatalogController();
      const token = await getAccessTokenSilently();
      return await action.addToBodySearchAction({
        dispatch,
        query,
        bodyProductSearchTemp: catalogState.bodyProductSearchTemp,
        translate,
        signal,
        token,
      });
    },
    [catalogState.bodyProductSearchTemp]
  );

  const removeFromBodySearch = useCallback(
    async (query: string) => {
      const signal = await abortCatalogController();
      const token = await getAccessTokenSilently();
      return await action.removeFromBodySearchAction({
        dispatch,
        query,
        bodyProductSearchTemp: catalogState.bodyProductSearchTemp,
        translate,
        signal,
        token,
      });
    },
    [catalogState.bodyProductSearchTemp]
  );

  const removeAllFromBodySearch = useCallback(async () => {
    const signal = await abortCatalogController();
    const token = await getAccessTokenSilently();
    return await action.removeAllFromBodySearchAction({
      dispatch,
      catalogState,
      translate,
      signal,
      token,
    });
  }, [catalogState]);

  const removeFromBodySearchFilter = useCallback(
    async (value: TQuerySearchArray) => {
      const signal = await abortCatalogController();
      const token = await getAccessTokenSilently();
      return await action.removeFromBodySearchFilterAction({
        dispatch,
        value,
        catalogState,
        translate,
        signal,
        token,
      });
    },
    [catalogState]
  );

  const applyFilters = useCallback(async () => {
    const signal = await abortCatalogController();
    const token = await getAccessTokenSilently();
    return await action.applyFiltersAction({
      dispatch,
      bodyProductSearchTemp: catalogState.bodyProductSearchTemp,
      connectedBrands,
      translate,
      signal,
      token,
    });
  }, [catalogState.bodyProductSearchTemp, connectedBrands]);

  const addBodyFilters = useCallback(
    (field: string, value: string[]) => {
      return action.addBodyFiltersAction({
        dispatch,
        field,
        value,
        bodyProductSearchTemp: catalogState.bodyProductSearchTemp,
      });
    },
    [catalogState.bodyProductSearchTemp]
  );

  const removeBodyFilters = useCallback(
    (field: string, value: string[]) => {
      return action.removeBodyFiltersAction({
        dispatch,
        field,
        value,
        bodyProductSearchTemp: catalogState.bodyProductSearchTemp,
      });
    },
    [catalogState.bodyProductSearchTemp]
  );

  const addBodyBrandOptions = useCallback(
    (value: string[]) => {
      return action.addBodyBrandOptionsAction({
        dispatch,
        value,
        bodyProductSearchTemp: catalogState.bodyProductSearchTemp,
      });
    },
    [catalogState.bodyProductSearchTemp]
  );

  const removeBodyBrandOptions = useCallback(
    (value: string[]) => {
      return action.removeBodyBrandOptionsAction({
        dispatch,
        value,
        bodyProductSearchTemp: catalogState.bodyProductSearchTemp,
      });
    },
    [catalogState.bodyProductSearchTemp]
  );

  const resetTempBodyFilters = useCallback(async () => {
    return dispatch({ type: CatalogTypes.RESET_TEMP_BODY_FILTERS });
  }, []);

  const resetBodyFilters = useCallback(async () => {
    const signal = await abortCatalogController();
    const token = await getAccessTokenSilently();
    return await action.resetBodyFiltersAction({
      dispatch,
      bodyProductSearchTemp: catalogState.bodyProductSearchTemp,
      translate,
      signal,
      token,
    });
  }, [catalogState.bodyProductSearchTemp]);

  const setQuerySearch = useCallback((query: string) => {
    return action.setQuerySearchAction({ dispatch, query });
  }, []);

  const resetPriceSlider = useCallback(
    async (type: string) => {
      const signal = await abortCatalogController();
      const token = await getAccessTokenSilently();
      return action.resetPriceSliderAction({
        dispatch,
        type,
        bodyProductSearchTemp: catalogState.bodyProductSearchTemp,
        translate,
        signal,
        token,
      });
    },
    [catalogState.bodyProductSearchTemp]
  );

  const setSortBy = useCallback(
    async (sortBy: string) => {
      const signal = await abortCatalogController();
      const token = await getAccessTokenSilently();
      return await action.setSortByAction({
        dispatch,
        sortBy,
        bodyProductSearchTemp: catalogState.bodyProductSearchTemp,
        translate,
        signal,
        token,
      });
    },
    [catalogState.bodyProductSearchTemp]
  );

  const setPriceSliders = useCallback((field: string, value: Array<number>) => {
    return action.setPriceSlidersAction({ dispatch, field, value });
  }, []);

  const getProductsByBrands = useCallback(
    async (brands: IBrand[]) => {
      const signal = await abortCatalogController();
      const token = await getAccessTokenSilently();
      return action.getProductsByBrandsAction({
        dispatch,
        connectedBrands: connectedBrands.map((brand) => brand.tenant.brand),
        brands,
        translate,
        bodyProductSearchTemp: catalogState.bodyProductSearchTemp,
        signal,
        token,
      });
    },
    [catalogState.bodyProductSearchTemp, connectedBrands]
  );

  const setFavorites = useCallback((userId: string | null) => {
    return action.setFavoritesAction({ dispatch, userId });
  }, []);

  const addLike = useCallback(
    async ({ productId }: IaddLikeActionProps) => {
      if (!dbUser?.id) return false;
      const token = await getAccessTokenSilently();
      if (!token) return false;
      return await action.addLikeAction({
        dispatch,
        userId: dbUser.id,
        productId,
        translate,
        token,
      });
    },
    [dbUser?.id]
  );

  const removeLike = useCallback(
    async ({ productId, favorites }: IremoveLikeActionProps) => {
      if (!dbUser?.id) return false;
      const token = await getAccessTokenSilently();
      if (!token) return false;
      return await action.removeLikeAction({
        dispatch,
        productId,
        favorites,
        userId: dbUser.id,
        translate,
        token,
      });
    },
    [dbUser?.id]
  );

  const addToQuerySearchArray = useCallback((value: TQuerySearchArray) => {
    return action.addToQuerySearchArrayAction({ dispatch, value });
  }, []);

  const removeFromQuerySearchArray = useCallback((value: TQuerySearchArray) => {
    return action.removeFromQuerySearchArrayAction({ dispatch, value });
  }, []);

  const setFormatView = useCallback((format: TCatalogFormatView) => {
    return dispatch({ type: CatalogTypes.SET_FORMAT_VIEW, payload: format });
  }, []);

  const memoProvider = useMemo(
    () => ({
      ...catalogState,
      getCatalog,
      getRelatedProducts,
      addMoreProductsToCatalog,
      addToBodySearch,
      removeFromBodySearch,
      resetCatalog,
      removeAllFromBodySearch,
      removeFromBodySearchFilter,
      getProduct,
      applyFilters,
      resetTempBodyFilters,
      addBodyFilters,
      removeBodyFilters,
      addBodyBrandOptions,
      removeBodyBrandOptions,
      resetBodyFilters,
      setQuerySearch,
      setSortBy,
      setPriceSliders,
      getProductsByBrands,
      setFavorites,
      addLike,
      removeLike,
      addToQuerySearchArray,
      removeFromQuerySearchArray,
      resetPriceSlider,
      setFormatView,
    }),
    [
      catalogState,
      getCatalog,
      getRelatedProducts,
      addMoreProductsToCatalog,
      addToBodySearch,
      removeFromBodySearch,
      resetCatalog,
      removeAllFromBodySearch,
      removeFromBodySearchFilter,
      getProduct,
      applyFilters,
      resetTempBodyFilters,
      addBodyFilters,
      removeBodyFilters,
      addBodyBrandOptions,
      removeBodyBrandOptions,
      resetBodyFilters,
      setQuerySearch,
      setSortBy,
      setPriceSliders,
      getProductsByBrands,
      setFavorites,
      addLike,
      removeLike,
      addToQuerySearchArray,
      removeFromQuerySearchArray,
      resetPriceSlider,
      setFormatView,
    ]
  );

  return (
    <CatalogContext.Provider value={memoProvider}>
      {props.children}
    </CatalogContext.Provider>
  );
}

export default memo(CatalogProvider);
